/**
 * Choix de « mon annonce » dans la carte d'action de la page d'une annonce : ce que chacune de mes annonces vaut pour
 * l'annonce visée, dans quel ordre les montrer, laquelle présélectionner, et la recherche.
 *
 * Bidirectionnel : l'expéditeur (meSide 'sender') choisit un de ses colis pour un trajet, le transporteur
 * (meSide 'carrier') un de ses trajets pour un colis. Les écrans affichent, ils ne décident rien d'autre.
 */

const num = (v) => (v === null || v === undefined || v === '' ? NaN : Number(v));
const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * Mon annonce face à l'annonce visée.
 * - sameRoute : même ville de départ et même ville d'arrivée
 * - tooHeavy : le colis pèse plus que la place restante du trajet — le serveur refuserait la proposition
 *   (`propositions.py`, place restante < poids du colis) ; une valeur absente ne bloque rien ici
 * - room : place restante du trajet (kg), weight : poids du colis (kg)
 */
export function listingFit(meSide, target, listing) {
  const trip = meSide === 'sender' ? target : listing;
  const shipment = meSide === 'sender' ? listing : target;
  const room = num(trip?.available_weight_kg);
  const weight = num(shipment?.weight_kg);
  return {
    sameRoute: !!listing && !!target
      && norm(listing.departure_city) === norm(target.departure_city)
      && norm(listing.arrival_city) === norm(target.arrival_city),
    tooHeavy: Number.isFinite(room) && Number.isFinite(weight) && weight > 0 && room < weight,
    room: Number.isFinite(room) ? room : null,
    weight: Number.isFinite(weight) ? weight : null,
  };
}

/**
 * Mes annonces dans l'ordre où les montrer : même trajet d'abord, puis les autres ; dans chaque groupe, celles qu'on
 * peut proposer, puis celles déjà proposées à cette annonce, puis les trop lourdes ; l'ordre reçu du serveur (la plus
 * récemment modifiée d'abord) départage le reste.
 * @returns {{ listing, fit, proposed: boolean, selectable: boolean }[]}
 */
export function rankListings(meSide, target, items, proposedIds = new Set()) {
  const rank = (e) => (e.fit.tooHeavy ? 2 : e.proposed ? 1 : 0);
  return (items || [])
    .map((listing, i) => {
      const fit = listingFit(meSide, target, listing);
      return { listing, fit, proposed: proposedIds.has(listing.id), selectable: !fit.tooHeavy, i };
    })
    .sort((a, b) => (a.fit.sameRoute === b.fit.sameRoute ? 0 : a.fit.sameRoute ? -1 : 1) || rank(a) - rank(b) || a.i - b.i)
    .map(({ listing, fit, proposed, selectable }) => ({ listing, fit, proposed, selectable }));
}

/**
 * Annonce présélectionnée : la première du même trajet qu'on peut encore proposer ; sinon, s'il n'y en a qu'une
 * qu'on peut choisir, celle-là ; sinon rien ('') — la liste reste ouverte et le membre choisit (un colis qui part
 * ailleurs n'est jamais choisi à sa place parmi plusieurs).
 */
export function bestListingId(ranked) {
  const best = ranked.find((e) => e.fit.sameRoute && e.selectable && !e.proposed);
  if (best) return best.listing.id;
  const selectable = ranked.filter((e) => e.selectable);
  return selectable.length === 1 ? selectable[0].listing.id : '';
}

/** Recherche dans mes annonces : chaque mot doit se trouver dans une ville, le titre, une catégorie ou une contrainte. */
export function matchesQuery(listing, query) {
  const words = norm(query).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const haystack = norm([
    listing.departure_city, listing.arrival_city, listing.title,
    ...(Array.isArray(listing.categories) ? listing.categories : []),
    ...(Array.isArray(listing.constraints) ? listing.constraints : []),
  ].join(' '));
  return words.every((w) => haystack.includes(w));
}
